import { openWebVault, type LocalVault } from '@signbento/storage'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { StoredDeletionReceipt } from './account-lifecycle.js'
import {
  createDeviceAccountVaults,
  onCurrentAccountChange,
  signInRequired,
  type BindOutcome,
  type DeviceAccountVaults,
} from './device-accounts.js'

type GuestAnswerOutcome = Exclude<BindOutcome, { kind: 'GUEST_CHOICE' }>

/**
 * Vault bootstrap — S0.9 walking skeleton.
 * The vault id is generated once per browser profile and kept in localStorage;
 * the DEK lives in the platform key store and records in IndexedDB.
 *
 * With the sign-in-once gate on (`VITE_SIGN_IN_REQUIRED=on`), each account owns its own vault
 * (`device-accounts.ts`): no vault opens until an account is current, binding a signed-in account
 * opens that account's vault, and every open tab follows the device's current account. With the
 * gate off, the single device vault opens as before.
 */

const VAULT_ID_KEY = 'signbento.vault_id'
/** The prefix `openWebVault` gives each vault's IndexedDB database. */
const VAULT_DATABASE_PREFIX = 'signbento-vault:'

/** What the sign-in gate shows after a release that ended this device's account. */
export type ReleaseNotice =
  | { readonly kind: 'deleted'; readonly receipt: StoredDeletionReceipt }
  | { readonly kind: 'switched' }
  /** Signed out from Settings: the gate says so and offers the same address again. */
  | { readonly kind: 'signed_out'; readonly email: string | null }

export interface VaultContextValue {
  readonly vault: LocalVault | null
  readonly vault_id: string | null
  readonly error: string | null
  readonly ready: boolean
  /**
   * The account this device is signed in to under the gate; `undefined` when the gate is off (the
   * vault is not owned by an account then).
   */
  readonly account?: string | null
  /** Shown on the sign-in gate after a deletion or account switch released this device. */
  readonly notice?: ReleaseNotice | null
  /** After a verified sign-in: open that account's vault, or ask the one-time guest question. */
  readonly bindAccount: (accountId: string) => Promise<BindOutcome>
  readonly chooseGuestRecords: (accountId: string, attach: boolean) => Promise<GuestAnswerOutcome>
  /**
   * Sign out on this device: nothing is wiped, and the vault closes at once so no screen keeps
   * the released account's records. A deletion receipt or switch note moves to the gate.
   */
  readonly signOutDevice: (options?: { readonly notice?: ReleaseNotice }) => void
}

export function resolveVaultId(storage: Pick<Storage, 'getItem' | 'setItem'>): string {
  const existing = storage.getItem(VAULT_ID_KEY)
  if (existing !== null && existing.length > 0) return existing
  const generated = crypto.randomUUID()
  storage.setItem(VAULT_ID_KEY, generated)
  return generated
}

export async function openAppVault(vaultId: string): Promise<LocalVault> {
  const handle = await openWebVault({
    vault_id: vaultId,
    clock: { now: () => new Date() },
  })
  return handle.vault
}

/** Every vault id with a database on this device (for repairing a damaged account map). */
async function knownVaultIds(): Promise<readonly string[]> {
  const listing = (indexedDB as { databases?: () => Promise<{ name?: string }[]> }).databases
  if (listing === undefined) return []
  const databases = await listing.call(indexedDB)
  return databases
    .map((database) => database.name ?? '')
    .filter((name) => name.startsWith(VAULT_DATABASE_PREFIX))
    .map((name) => name.slice(VAULT_DATABASE_PREFIX.length))
}

/**
 * `localStorage`, or null where the browser refuses it (site data blocked: reading the property
 * itself throws). Without it no vault can be found or opened, and the shell says why.
 */
function deviceStorage(): Storage | null {
  try {
    const storage = window.localStorage
    storage.getItem(VAULT_ID_KEY)
    return storage
  } catch {
    return null
  }
}

/** The reason a vault could not open when the browser would not even hand out storage. */
export const STORAGE_BLOCKED = 'storage_blocked'

/**
 * Whether this browser lets the app keep data on the device: storage readable and an IndexedDB
 * database that opens. Asked before a sign-in code is sent behind the gate, so a private window
 * or blocked site data is explained first, not after the code was used. A probe that never
 * answers counts as a yes; the vault open that follows still reports a real failure.
 */
export async function probeDeviceStorage(timeoutMs = 3000): Promise<boolean> {
  if (deviceStorage() === null) return false
  if (typeof indexedDB === 'undefined') return false
  return new Promise<boolean>((resolve) => {
    const timer = window.setTimeout(() => resolve(true), timeoutMs)
    const done = (ok: boolean) => {
      window.clearTimeout(timer)
      resolve(ok)
    }
    try {
      const request = indexedDB.open('signbento-storage-probe')
      request.onsuccess = () => {
        request.result.close()
        done(true)
      }
      request.onerror = () => done(false)
      request.onblocked = () => done(true)
    } catch {
      done(false)
    }
  })
}

const unbound = async (): Promise<never> => {
  throw new Error('vault context is not mounted')
}

const VaultContext = createContext<VaultContextValue>({
  vault: null,
  vault_id: null,
  error: null,
  ready: false,
  bindAccount: unbound,
  chooseGuestRecords: unbound,
  signOutDevice: () => undefined,
})

interface VaultState {
  readonly vault: LocalVault | null
  readonly vault_id: string | null
  readonly error: string | null
  readonly ready: boolean
  readonly account: string | null
  readonly notice: ReleaseNotice | null
}

const SIGNED_OUT: VaultState = {
  vault: null,
  vault_id: null,
  error: null,
  ready: true,
  account: null,
  notice: null,
}

export function VaultProvider({ children }: { readonly children: ReactNode }) {
  const gated = signInRequired()
  const storage = useMemo(() => deviceStorage(), [])
  const accounts = useMemo<DeviceAccountVaults | null>(
    () =>
      gated && storage !== null
        ? createDeviceAccountVaults({
            storage,
            open: openAppVault,
            newVaultId: () => crypto.randomUUID(),
            knownVaultIds,
          })
        : null,
    [gated, storage],
  )
  const [state, setState] = useState<VaultState>({ ...SIGNED_OUT, ready: false })
  // Every open, bind and release takes a new generation; a slower, older open never lands.
  const generation = useRef(0)

  /** Opens whatever account the device says is current, unless a newer change superseded it. */
  const openCurrent = useCallback(async () => {
    if (accounts === null) return
    const mine = (generation.current += 1)
    try {
      const opened = await accounts.openCurrent()
      if (generation.current !== mine) return
      setState({
        vault: opened?.vault ?? null,
        vault_id: opened?.vault_id ?? null,
        error: null,
        ready: true,
        account: opened === null ? null : accounts.current(),
        notice: null,
      })
    } catch (error) {
      if (generation.current !== mine) return
      setState({
        ...SIGNED_OUT,
        error: error instanceof Error ? error.message : 'vault_unavailable',
      })
    }
  }, [accounts])

  useEffect(() => {
    if (storage === null) {
      setState({ ...SIGNED_OUT, error: STORAGE_BLOCKED })
      return
    }
    if (accounts !== null) {
      void openCurrent()
      // Another tab signed out, signed in or switched: follow it, never keep the old vault.
      return onCurrentAccountChange(window, (accountId) => {
        if (accountId === null) {
          generation.current += 1
          setState(SIGNED_OUT)
          return
        }
        void openCurrent()
      })
    }
    let cancelled = false
    void (async () => {
      try {
        const vaultId = resolveVaultId(storage)
        const vault = await openAppVault(vaultId)
        if (!cancelled) {
          setState({
            vault,
            vault_id: vaultId,
            error: null,
            ready: true,
            account: null,
            notice: null,
          })
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            ...SIGNED_OUT,
            error: error instanceof Error ? error.message : 'vault_unavailable',
          })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [accounts, openCurrent, storage])

  const settle = useCallback(
    <T extends BindOutcome>(accountId: string, mine: number, outcome: T): T => {
      if (outcome.kind === 'BOUND' && generation.current === mine) {
        setState({
          vault: outcome.vault,
          vault_id: outcome.vault_id,
          error: null,
          ready: true,
          account: accountId,
          notice: null,
        })
      }
      return outcome
    },
    [],
  )

  const bindAccount = useCallback(
    async (accountId: string): Promise<BindOutcome> => {
      // Gate off: the device vault is the vault, whoever signs in (today's behaviour).
      if (accounts === null) {
        if (state.vault === null || state.vault_id === null) throw new Error('vault_unavailable')
        return { kind: 'BOUND', vault: state.vault, vault_id: state.vault_id }
      }
      const mine = (generation.current += 1)
      return settle(accountId, mine, await accounts.bind(accountId))
    },
    [accounts, settle, state.vault, state.vault_id],
  )

  const chooseGuestRecords = useCallback(
    async (accountId: string, attach: boolean): Promise<GuestAnswerOutcome> => {
      if (accounts === null) throw new Error('guest records are only offered behind the gate')
      const mine = (generation.current += 1)
      return settle(accountId, mine, await accounts.resolveGuestChoice(accountId, attach))
    },
    [accounts, settle],
  )

  const signOutDevice = useCallback(
    (options?: { readonly notice?: ReleaseNotice }) => {
      if (accounts === null) return
      void accounts.release()
      generation.current += 1
      setState({ ...SIGNED_OUT, notice: options?.notice ?? null })
    },
    [accounts],
  )

  const value = useMemo<VaultContextValue>(
    () => ({
      vault: state.vault,
      vault_id: state.vault_id,
      error: state.error,
      ready: state.ready,
      ...(accounts === null ? {} : { account: state.account, notice: state.notice }),
      bindAccount,
      chooseGuestRecords,
      signOutDevice,
    }),
    [state, accounts, bindAccount, chooseGuestRecords, signOutDevice],
  )

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>
}

export function useVault(): VaultContextValue {
  return useContext(VaultContext)
}
